import type { ChatMessage, ClientEvent, Mode, PageAction, PageContext, ServerEvent } from "../../shared/protocol";

/**
 * A page action as the page registers it:
 *   (window.conversationalAgentActions ??= []).push({ name, description, parameters, run(args) {…} })
 * With reveal, the chat steps aside first so the visitor sees what happens.
 */
export interface RegisteredAction extends PageAction {
  run(args: Record<string, unknown>): unknown;
  reveal?: boolean;
}

function registeredActions(): RegisteredAction[] {
  const list = (window as unknown as { conversationalAgentActions?: unknown }).conversationalAgentActions;
  return Array.isArray(list) ? list.filter((a): a is RegisteredAction => !!a && typeof a.name === "string" && typeof a.run === "function") : [];
}

// One client per site and page, shared by every widget on it (a launcher and
// an inline chat show the same conversation). Talks to the conversation over
// a WebSocket and keeps the visible state.

export interface ChatState {
  messages: ChatMessage[];
  /** The visitor's messages shown right away, until the server confirms them. */
  pending: ChatMessage[];
  /** An assistant reply that is still streaming in. */
  draft?: ChatMessage;
  typing?: "assistant" | "owner";
  mode: Mode;
  /** Who is in the chat when mode is "human". */
  agentName: string;
  ownerName: string;
  siteName: string;
  greeting?: string;
  connected: boolean;
  error?: string;
}

type Listener = (state: ChatState) => void;

const clients = new Map<string, ChatClient>();

export function getClient(api: string, site: string): ChatClient {
  const key = `${api}|${site}`;
  let client = clients.get(key);
  if (!client) clients.set(key, (client = new ChatClient(api, site)));
  return client;
}

interface SiteInfo {
  name: string;
  ownerName: string;
  greeting: string | null;
  turnstileSiteKey?: string | null;
}

export class ChatClient {
  /** Set by a widget on the page: solves the site's Turnstile challenge. */
  challenge?: (siteKey: string) => Promise<string>;
  /** Set by a widget on the page: clears the view before a revealing action. */
  reveal?: () => Promise<void>;
  private info: Promise<SiteInfo | undefined>;
  state: ChatState = { messages: [], pending: [], mode: "ai", agentName: "", ownerName: "", siteName: "", connected: false };
  private listeners = new Set<Listener>();
  private ws?: WebSocket;
  private outbox: ClientEvent[] = [];
  private retry = 0;
  private failedOpens = 0;
  private creating?: Promise<string>;
  private storageKey: string;

  constructor(private api: string, private site: string) {
    this.storageKey = `conversationalagent:${site}`;
    this.info = fetch(`${api}/api/sites/${encodeURIComponent(site)}`)
      .then((r) => (r.ok ? (r.json() as Promise<SiteInfo>) : undefined))
      .catch(() => undefined);
    this.info.then((cfg) => {
      if (cfg) this.set({ siteName: cfg.name, ownerName: cfg.ownerName, greeting: this.state.greeting ?? cfg.greeting ?? undefined });
    });
    // Pick up an earlier conversation straight away so its history shows.
    if (this.storedId()) this.connect();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  async send(text: string): Promise<void> {
    text = text.trim();
    if (!text) return;
    const id = localId();
    this.set({
      error: undefined,
      pending: [...this.state.pending, { id, role: "visitor", text, at: Date.now() }],
      typing: this.state.mode === "ai" ? "assistant" : this.state.typing,
    });
    this.push({ type: "message", text, page: pageContext(), id });
    if (!this.ws) await this.connect();
  }

  /** Starts over: the visitor's next message opens a new conversation. */
  reset(): void {
    const ws = this.ws;
    this.ws = undefined;
    if (ws) {
      ws.onmessage = null;
      ws.close();
    }
    this.outbox = [];
    this.forget();
    this.set({ error: undefined, typing: undefined, draft: undefined, pending: [], connected: false });
  }

  private push(event: ClientEvent) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(event));
    else this.outbox.push(event);
  }

  private async connect(): Promise<void> {
    let id = this.storedId();
    if (!id) {
      try {
        id = await this.createConversation();
      } catch (err) {
        const reason = err instanceof Error ? err.message : "";
        this.outbox = [];
        this.set({
          pending: [],
          typing: undefined,
          error:
            reason === "challenge failed"
              ? "We couldn't confirm you're not a bot. Please try again."
              : reason === "blocked"
                ? "This chat has been blocked."
                : "Couldn't reach the chat. Please try again.",
        });
        return;
      }
    }
    const url = new URL(`${this.api}/api/conversations/${id}/ws`);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("site", this.site);
    const ws = new WebSocket(url.toString());
    this.ws = ws;
    let opened = false;
    ws.onopen = () => {
      opened = true;
      this.retry = 0;
      this.failedOpens = 0;
      ws.send(JSON.stringify({ type: "hello", page: pageContext() } satisfies ClientEvent));
      for (const e of this.outbox.splice(0)) ws.send(JSON.stringify(e));
      this.set({ connected: true });
    };
    ws.onmessage = (e) => this.handle(JSON.parse(String(e.data)) as ServerEvent);
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = undefined;
      this.set({ connected: false, typing: undefined });
      if (!opened && ++this.failedOpens >= 2) {
        // The socket keeps failing: if the server no longer knows this
        // conversation, start a fresh one with the next message.
        fetch(`${this.api}/api/conversations/${id}?site=${encodeURIComponent(this.site)}`)
          .then((r) => (r.status === 404 ? this.forget() : this.reconnectLater()))
          .catch(() => this.reconnectLater());
        return;
      }
      this.reconnectLater();
    };
  }

  private reconnectLater() {
    const delay = Math.min(15000, 1000 * 2 ** this.retry++);
    setTimeout(() => {
      if (!this.ws) this.connect();
    }, delay);
  }

  private createConversation(): Promise<string> {
    this.creating ??= this.info
      .then(async (cfg) => {
        const key = cfg?.turnstileSiteKey;
        // If the challenge can't run (blocked script, unlisted domain), try without:
        // the Worker decides whether a token is required.
        const turnstile = key && this.challenge ? await this.challenge(key).catch(() => undefined) : undefined;
        return fetch(`${this.api}/api/conversations`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ site: this.site, turnstile }),
        });
      })
      .then(async (r) => {
        if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? String(r.status));
        const { id } = (await r.json()) as { id: string };
        try {
          localStorage.setItem(this.storageKey, id);
        } catch {
          // storage blocked: the conversation lasts for this page view
        }
        this.memoryId = id;
        return id;
      })
      .finally(() => (this.creating = undefined));
    return this.creating;
  }

  private memoryId?: string;

  private storedId(): string | undefined {
    try {
      return localStorage.getItem(this.storageKey) ?? this.memoryId;
    } catch {
      return this.memoryId;
    }
  }

  private forget() {
    this.memoryId = undefined;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
    this.set({ messages: [], mode: "ai" });
  }

  private handle(event: ServerEvent) {
    switch (event.type) {
      case "history":
        this.set({
          messages: event.messages,
          pending: this.state.pending.filter((p) => !event.messages.some((m) => m.id === p.id)),
          mode: event.mode,
          ownerName: event.ownerName,
          agentName: event.ownerName,
          greeting: event.greeting ?? this.state.greeting,
          draft: undefined,
        });
        break;
      case "message": {
        const messages = this.state.messages.filter((m) => m.id !== event.message.id);
        messages.push(event.message);
        const draft = this.state.draft?.id === event.message.id ? undefined : this.state.draft;
        const typing = event.message.role === "assistant" || event.message.role === "owner" ? undefined : this.state.typing;
        const pending = this.state.pending.filter((p) => p.id !== event.message.id);
        this.set({ messages, draft, typing, pending });
        break;
      }
      case "delta": {
        const draft = this.state.draft?.id === event.id ? this.state.draft : { id: event.id, role: "assistant" as const, text: "", at: Date.now() };
        this.set({ draft: { ...draft, text: draft.text + event.text }, typing: undefined });
        break;
      }
      case "typing":
        this.set({ typing: event.role });
        break;
      case "mode":
        this.set({ mode: event.mode, agentName: event.ownerName, typing: undefined });
        break;
      case "error":
        // The server turned the visitor's message down (too fast, blocked): it won't appear.
        this.set({ error: event.message, typing: undefined, pending: [] });
        break;
      case "action":
        this.runAction(event.name, event.args);
        break;
    }
  }

  private async runAction(name: string, args: Record<string, unknown>) {
    const action = registeredActions().find((a) => a.name === name);
    if (!action) return;
    try {
      if (action.reveal) await this.reveal?.();
      await action.run(args ?? {});
    } catch (err) {
      console.error(`conversational-agent: action ${name} failed`, err);
    }
  }

  private set(patch: Partial<ChatState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }
}

// randomUUID only exists on https pages.
function localId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** What the agent should know about the page the visitor is looking at. */
export function pageContext(): PageContext {
  const root = document.querySelector("main") ?? document.querySelector("article") ?? document.body;
  const text = (root?.innerText ?? "").replace(/\s+/g, " ").trim().slice(0, 4000);
  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content;
  const actions = registeredActions().map(({ name, description, parameters }) => ({ name, description, parameters }));
  return { url: location.href, title: document.title, description, text, actions: actions.length ? actions : undefined };
}
